import { rtkApi, GET_PROFILE } from '@/shared/api';
import { SuccessApiResponse } from '@/shared/types/common';
import { createFormData } from '@/shared/lib/utils/createFormData';
import type {
  IProfileResponse,
  IProfileUpdate,
  IChangePasswordRequest,
  IChangePasswordResponse,
  IDeleteAccountRequest,
  IDeleteAccountResponse,
  IRequestAccountDeletionByEmailRequest,
  IEmergencyContactUpdate,
} from './types';

export const profileApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getMyProfile: build.query<IProfileResponse, void>({
      query: () => ({
        url: '/my-profile',
      }),
      transformResponse: (response: SuccessApiResponse<{ user: IProfileResponse }>) =>
        response.data.user,
      providesTags: [GET_PROFILE],
    }),
    updateMyProfile: build.mutation<IProfileResponse, IProfileUpdate>({
      query: (data) => ({
        url: '/my-profile',
        method: 'PUT',
        body: createFormData(data),
      }),
      transformResponse: (response: SuccessApiResponse<{ profile: IProfileResponse }>) =>
        response.data.profile,
      invalidatesTags: [GET_PROFILE],
    }),
    updateMyEmergencyContact: build.mutation<IProfileResponse, IEmergencyContactUpdate>({
      query: (data) => ({
        url: '/my-profile/emergency-contact',
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: SuccessApiResponse<{ user: IProfileResponse }>) =>
        response.data.user,
      invalidatesTags: [GET_PROFILE],
    }),
    changePassword: build.mutation<IChangePasswordResponse, IChangePasswordRequest>({
      query: (data) => ({
        url: '/my-profile/change-password',
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: SuccessApiResponse<IChangePasswordResponse>) => response.data,
    }),
    // Ответ 202: учётка уже закрыта, удаление идёт в фоне. Неверный пароль — 400, сессия жива,
    // поэтому перехватчик 401 здесь не нужен: после успеха выходом управляет вызывающий
    deleteMyAccount: build.mutation<IDeleteAccountResponse, IDeleteAccountRequest>({
      query: (data) => ({
        url: '/student/account/deletion',
        method: 'POST',
        body: data,
      }),
      extraOptions: { skipAuthRedirect: true },
      transformResponse: (response: SuccessApiResponse<IDeleteAccountResponse>) => response.data,
    }),
    // Для тех, кто не помнит пароль: на почту уходит ссылка подтверждения, до перехода по ней
    // с учётной записью ничего не происходит. Запрос публичный и идёт без токена
    // (ANONYMOUS_ENDPOINTS в rtkApi). Ответ — 202 с безличным текстом для анонимной формы;
    // вошедшему LMS показывает его собственный адрес, поэтому тело ответа не нужно
    requestAccountDeletionByEmail: build.mutation<void, IRequestAccountDeletionByEmailRequest>({
      query: (data) => ({
        url: '/account/deletion/web-request',
        method: 'POST',
        body: data,
      }),
      extraOptions: { skipAuthRedirect: true },
    }),
  }),

});

export const {
  useGetMyProfileQuery,
  useUpdateMyProfileMutation,
  useUpdateMyEmergencyContactMutation,
  useChangePasswordMutation,
  useDeleteMyAccountMutation,
  useRequestAccountDeletionByEmailMutation,
} = profileApi;
